import { useStore } from '@/hooks/useStore';
import { CloudUpload, XIcon } from 'lucide-react';
import { flowResult } from 'mobx';
import React, { useRef, useState, useEffect, ReactNode } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Label } from '../common/Label';
import { Input } from '../common/Input';
import { cn } from '@/utils/utils';
import { FormMessage } from '../common/Form';
import { validateImageFile } from '@/utils/validate';

interface ImageUploadProps {
    filedName: string,
    label?: string,
    required?: boolean,
    form: UseFormReturn<any>,
    caption?: string | ReactNode,
    className?: string,
    urlImagePreview?: string,
    disabled?: boolean,
    isUploadOCR?: boolean
}

const ImageUpload: React.FC<ImageUploadProps> = ({
    filedName,
    label = '',
    required = false,
    caption = '',
    form,
    className,
    urlImagePreview,
    disabled = false,
    isUploadOCR = false
}) => {
    const { setValue, trigger, formState: { errors } } = form;
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(urlImagePreview || null);

    const {
        fileStore: { uploadFile },
        accountStore: { uploadOCR },
        modalStore: { showAlertModal }
    } = useStore();

    // Handle image change
    const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];

        if (file) {
            const { isValid, error } = validateImageFile(file, t);

            if (!isValid) {
                showAlertModal({
                    id: 'error',
                    type: 'error',
                    content: error
                });
                e.target.value = '';
                return;
            }

            const res = await flowResult(
                isUploadOCR ?
                    uploadOCR({ files: file }) :
                    uploadFile({ files: file })
            );

            if (res?.ok) {
                const newImagePreview = URL.createObjectURL(file);
                setImagePreview(newImagePreview);
                if (res.data?.length) {
                    setValue(filedName, res.data[0].id);
                    trigger(filedName);
                }
            }
        }

        e.target.value = '';
    };

    // Remove image
    const handleRemoveImage = () => {
        setImagePreview(null);
        setValue(filedName, undefined);
        trigger(filedName);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Revoke object URL when component unmount
    useEffect(() => {
        return () => {
            if (imagePreview) {
                URL.revokeObjectURL(imagePreview);
            }
        };
    }, [imagePreview]);

    useEffect(() => {
        setImagePreview(urlImagePreview || null);
    }, [urlImagePreview]);

    return (
        <div className='flex flex-col'>
            {label && (
                <Label required={required}>
                    {label}
                </Label>
            )}
            <Input
                type='file'
                accept='image/*'
                ref={fileInputRef}
                onChange={handleImageChange}
                className='hidden'
            />
            {imagePreview ?
                (
                    <div className={cn(
                        'relative h-fit w-full border border-input rounded-lg',
                        !!errors[filedName] && 'border-primaryWeb',
                        className
                    )}
                    >
                        <img
                            src={imagePreview}
                            alt=''
                            className='h-48 w-full object-contain rounded-lg'
                        />
                        {caption && (
                            <div className=' bottom-0 left-0 flex items-center justify-center bg-background-12 w-full rounded-b-md h-8'>
                                <p className='line-clamp-1 p-1'>{caption}</p>
                            </div>
                        )}
                        {!disabled && (
                            <button
                                type='button'
                                title='Remove image'
                                onClick={handleRemoveImage}
                                className='absolute top-1 right-1 bg-black bg-opacity-50 rounded-full p-1 shadow-lg'
                            >
                                <XIcon className='w-3 h-3 text-white' />
                            </button>
                        )}
                    </div>
                ) :
                (
                    <button
                        type='button'
                        disabled={disabled}
                        onClick={() => fileInputRef.current?.click()}
                        className={cn(
                            'w-full h-fit flex flex-col items-center justify-center rounded-lg border border-input transition-colors duration-300',
                            !!errors[filedName] && 'border-primaryWeb',
                            disabled ? 'cursor-not-allowed' : 'hover:border-primaryWeb',
                            className
                        )}
                    >
                        <div className='flex flex-col items-center justify-center h-48'>
                            <CloudUpload className='size-10 text-primaryWeb' />
                            <p className='mt-1 text-placeholder'>{t('field.choose_image')}</p>
                        </div>
                        {caption && (
                            <div className='flex items-center justify-center bg-background-12 w-full rounded-b-lg h-8'>
                                <p className='line-clamp-1 p-1'>{caption}</p>
                            </div>
                        )}
                    </button>
                )}
            {errors[filedName] && (
                <FormMessage className='mt-0.5'>
                    {errors[filedName]?.message as string}
                </FormMessage>
            )}
        </div>
    );
};

export default ImageUpload;
